import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import useScrollAnimation from '../../hooks/useScrollAnimation';
import useAutoHeightAdjustment from '../../hooks/useAutoHeightAdjustment';
import { getServiceData } from '../../data/serviceData';
import { SERVICES_LIST } from '../../constants/services';
import FocusTrap from '../../utils/focusTrap';
import ReCaptchaModal from '../ui/ReCaptchaModal';
import LazyServiceBanner from '../ui/LazyServiceBanner';
import emailjsConfig from '../../config/emailjsConfig';
import { 
  allServices, 
  validateForm, 
  focusFirstError, 
  getDefaultFormState, 
  getDefaultFormErrors 
} from '../../utils/formUtils';
import { sendEmail } from '../../utils/emailService';
import { useFormValidation } from '../../hooks/useFormValidation';
import { 
  FaTimes,
  FaBolt,
  FaCreditCard,
  FaHome,
  FaTimesCircle
} from 'react-icons/fa';
import './Services.css';
import '../ui/CheckboxContainer.css';

const Services = () => {
  const navigate = useNavigate();
  const titleRef = useScrollAnimation();
  const ctaTextRef = useScrollAnimation();
  const servicesCtaRef = useScrollAnimation();
  const gcPromotionRef = useScrollAnimation();
  const financingHeaderRef = useScrollAnimation();
  
  // Use the auto height adjustment hook for financing section
  const { sameHeightRef: financingHeightRef, refresh: refreshFinancingHeights } = useAutoHeightAdjustment({
    containerSelector: '.financing-main.same-height',
    elementSelector: '[data-same-height]',
    debounceDelay: 200,
    observeChildList: true,
    observeAttributes: true,
    observeCharacterData: true,
    subtree: true
  });
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalService, setModalService] = useState(null);
  const lastFocusedElementRef = useRef(null);
  const modalRef = useRef(null);
  const focusTrapRef = useRef(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [isTransitioning, setIsTransitioning] = useState(false);
  // Use custom form validation hook
  const {
    formData,
    formErrors,
    setFormData,
    setFormErrors,
    handleChange,
    handleKeyDown,
    clearFieldError
  } = useFormValidation(getDefaultFormState(), getDefaultFormErrors());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [successMessageFading, setSuccessMessageFading] = useState(false);
  const [showCaptchaModal, setShowCaptchaModal] = useState(false);
  const [serviceSearch, setServiceSearch] = useState('');
  const [showServiceResults, setShowServiceResults] = useState(false);
  const [selectedOptionIndex, setSelectedOptionIndex] = useState(-1);
  
  // Touch/swipe functionality for mobile
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  // Function to scroll to financing form and focus on it
  const scrollToFinancingForm = () => {
    const financingForm = document.querySelector('.financing-section .contact-form-card');
    
    if (financingForm) {
      financingForm.scrollIntoView({ 
        behavior: 'smooth',
        block: 'nearest'
      });
      
      // Focus on the form after scrolling
      setTimeout(() => {
        const firstInput = financingForm.querySelector('input, textarea, select');
        if (firstInput) {
          firstInput.focus();
        } else {
          financingForm.focus();
        }
      }, 500); // Wait for scroll animation to complete
    }
  };
  const successTimeoutRef = useRef(null);
  const autoPlayRef = useRef(null);
  const financingSelectedServicesRef = useRef(null);
  const serviceSearchRef = useRef(null);

  // Services data is now imported from shared utilities

  // Get available services (exclude already selected ones)
  const availableServices = useMemo(() => {
    return allServices.filter(service => 
      !formData.services.includes(service.value)
    );
  }, [formData.services]);

  // Get filtered services based on search input
  const filteredServices = useMemo(() => {
    if (!serviceSearch.trim()) return availableServices;
    return availableServices.filter(service =>
      service.label.toLowerCase().includes(serviceSearch.toLowerCase())
    );
  }, [availableServices, serviceSearch]);

  // Handle adding a service to the selected list
  const handleAddService = (serviceValue) => {
    setFormData(prev => ({
      ...prev,
      services: [...prev.services, serviceValue]
    }));
    
    // Clear error when user selects a service
    if (formErrors.services) {
      setFormErrors(prev => ({
        ...prev,
        services: ''
      }));
    }
    
    // Trigger height refresh after service is added
    setTimeout(() => {
      if (refreshFinancingHeights) {
        refreshFinancingHeights();
      }
    }, 100);
  };

  // Handle removing a service from the selected list
  const handleRemoveService = (serviceValue) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.filter(service => service !== serviceValue)
    }));
    
    // Trigger height refresh after service is removed
    setTimeout(() => {
      if (refreshFinancingHeights) {
        refreshFinancingHeights();
      }
    }, 100);
  };

  // Handle service search input change
  const handleServiceSearchChange = (e) => {
    const value = e.target.value;
    setServiceSearch(value);
    // Always show results when there are available services, regardless of search input
    setShowServiceResults(availableServices.length > 0);
    setSelectedOptionIndex(-1); // Reset selection when typing
    
    // Clear services error when user starts typing
    if (formErrors.services) {
      setFormErrors(prev => ({
        ...prev,
        services: ''
      }));
    }
  };

  // Handle clearing service search
  const handleClearServiceSearch = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setServiceSearch('');
    setShowServiceResults(false);
    setSelectedOptionIndex(-1);
    // Refocus the input after clearing
    if (serviceSearchRef.current) {
      serviceSearchRef.current.focus();
    }
  };

  // Handle service search input focus
  const handleServiceSearchFocus = () => {
    // Always show results when focused if there are available services
    setShowServiceResults(availableServices.length > 0);
    setSelectedOptionIndex(-1); // Reset selection on focus
  };

  // Handle service search input blur
  const handleServiceSearchBlur = (e) => {
    // Check if the blur is caused by clicking on a service result
    const relatedTarget = e.relatedTarget;
    if (relatedTarget && relatedTarget.classList.contains('service-search-result')) {
      return; // Don't hide results if clicking on a service option
    }
    
    // Delay hiding results to allow for click events
    setTimeout(() => {
      setShowServiceResults(false);
      setSelectedOptionIndex(-1); // Reset selection on blur
    }, 200);
  };

  // Handle service selection from search results
  const handleServiceSelect = (serviceValue) => {
    handleAddService(serviceValue);
    setServiceSearch('');
    setSelectedOptionIndex(-1); // Reset selection
    
    // Keep dropdown open and maintain focus for continued selection
    setTimeout(() => {
      if (serviceSearchRef.current) {
        serviceSearchRef.current.focus();
        // Always show results - the availableServices will update automatically
        // due to the useMemo dependency on formData.services
        setShowServiceResults(true);
      }
    }, 100);
  };

  // Handle keyboard navigation for search results
  const handleServiceSearchKeyDown = (e) => {
    const currentResults = filteredServices.length > 0 ? filteredServices : availableServices;
    
    if (e.key === 'Escape') {
      setShowServiceResults(false);
      setServiceSearch('');
      setSelectedOptionIndex(-1);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (currentResults.length > 0) {
        const nextIndex = selectedOptionIndex < currentResults.length - 1 ? selectedOptionIndex + 1 : 0;
        setSelectedOptionIndex(nextIndex);
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (currentResults.length > 0) {
        const prevIndex = selectedOptionIndex > 0 ? selectedOptionIndex - 1 : currentResults.length - 1;
        setSelectedOptionIndex(prevIndex);
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (selectedOptionIndex >= 0 && selectedOptionIndex < currentResults.length) {
        const selectedService = currentResults[selectedOptionIndex];
        handleServiceSelect(selectedService.value);
      }
    }
    // Tab key is not handled here - it will naturally move to the next focusable element
  };

  // Refresh same-height when status messages change
  useEffect(() => {
    if (refreshFinancingHeights) {
      // Small delay to ensure DOM has updated
      setTimeout(() => {
        refreshFinancingHeights();
      }, 50);
    }
  }, [submitStatus, refreshFinancingHeights]);

  // Refresh same-height when services change
  useEffect(() => {
    if (refreshFinancingHeights) {
      // Small delay to ensure DOM has updated
      setTimeout(() => {
        refreshFinancingHeights();
      }, 100);
    }
  }, [formData.services, refreshFinancingHeights]);

  // Additional effect to watch financingSelectedServicesRef changes
  useEffect(() => {
    if (financingSelectedServicesRef.current && refreshFinancingHeights) {
      const observer = new MutationObserver(() => {
        // Debounced refresh for service tag changes
        setTimeout(() => {
          refreshFinancingHeights();
        }, 50);
      });

      observer.observe(financingSelectedServicesRef.current, {
        childList: true,
        subtree: true,
        characterData: true
      });

      return () => {
        observer.disconnect();
      };
    }
  }, [formData.services.length, refreshFinancingHeights]);

  // Auto-remove success message after 5 seconds
  useEffect(() => {
    if (submitStatus === 'success') {
      // Clear any existing timeout
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }
      
      // Start fade-out after 4.5 seconds
      successTimeoutRef.current = setTimeout(() => {
        setSuccessMessageFading(true);
        
        // Remove message completely after fade animation
        setTimeout(() => {
          setSubmitStatus(null);
          setSuccessMessageFading(false);
        }, 500); // Match CSS animation duration
      }, 4500);
    }

    return () => {
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }
    };
  }, [submitStatus]);


  // Validate form using shared utility
  const validateFormData = () => {
    const validation = validateForm(formData, false); // Don't include message validation for financing form
    setFormErrors(validation.errors);
    return { isValid: validation.isValid, errors: validation.errors };
  };

  const handleCaptchaVerify = async (value) => {
    // CAPTCHA completed, now send the email
    await sendEmailData();
    setShowCaptchaModal(false);
  };

  const sendEmailData = async () => {
    setIsSubmitting(true);
    setSubmitStatus(null);
    
    try {
      // Prepare email data for EmailJS
      const emailData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        service: Array.isArray(formData.services) ? formData.services.join(', ') : formData.services || '',
        message: formData.message || 'Financing inquiry',
        smsConsent: formData.smsConsent ? 'Yes' : 'No',
        type: 'contact'
      };
      
      // Add timeout to prevent hanging
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Email sending timeout')), 30000); // 30 second timeout
      });
      
      const result = await Promise.race([
        sendEmail(emailData, 'contact'),
        timeoutPromise
      ]);
      
      if (result.success) {
        setIsSubmitting(false);
        setSubmitStatus('success');
        setFormData(getDefaultFormState());
        setFormErrors(getDefaultFormErrors());
        
        // Focus on success message after a short delay to ensure it's rendered
        setTimeout(() => {
          const successMessage = document.querySelector('.status-message.success');
          if (successMessage) {
            successMessage.focus();
            successMessage.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      setIsSubmitting(false);
      setSubmitStatus('error');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form using shared utility
    const validation = validateFormData();
    if (!validation.isValid) {
      setSubmitStatus('validation-error');
      focusFirstError(validation.errors, { serviceSearch: serviceSearchRef.current }, 'financing');
      return;
    }
    
    // Show reCAPTCHA modal for verification
    setShowCaptchaModal(true);
  };

  const handleServiceClick = useCallback((service, event) => {
    // Store the element that triggered the modal
    lastFocusedElementRef.current = event?.currentTarget || document.activeElement;
    const serviceData = getServiceData(service);
    setModalService({ service, data: serviceData });
    setIsModalOpen(true);
  }, []);

  const closeModal = () => {
    setIsModalOpen(false);
    setModalService(null);
    
    // Remove modal-open class from body
    document.body.classList.remove('modal-open');
    
    // Deactivate focus trap
    if (focusTrapRef.current) {
      focusTrapRef.current.deactivate();
      focusTrapRef.current = null;
    }
    
    // Return focus to the element that triggered the modal
    if (lastFocusedElementRef.current) {
      lastFocusedElementRef.current.focus();
    }
  };

  // Focus trap management for service modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (isModalOpen && e.key === 'Escape') {
        closeModal();
      }
    };

    if (isModalOpen && modalRef.current) {
      // Prevent body scroll
      document.body.classList.add('modal-open');
      
      // Add escape key listener
      document.addEventListener('keydown', handleKeyDown);
      
      // Set up focus trap after modal is rendered
      setTimeout(() => {
        if (modalRef.current && !focusTrapRef.current) {
          focusTrapRef.current = new FocusTrap(modalRef.current);
          focusTrapRef.current.activate();
        }
      }, 0);
    } else {
      // Remove escape key listener
      document.removeEventListener('keydown', handleKeyDown);
      
      // Cleanup on unmount or when modal closes
      if (focusTrapRef.current) {
        focusTrapRef.current.deactivate();
        focusTrapRef.current = null;
      }
    }

    // Cleanup on unmount
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      if (focusTrapRef.current) {
        focusTrapRef.current.deactivate();
        focusTrapRef.current = null;
      }
    };
  }, [isModalOpen]);

  // Services list for carousel
  const services = useMemo(() => SERVICES_LIST.slice(0, 8), []);

  // Carousel navigation functions with smooth infinite loop
  const nextSlide = useCallback(() => {
    if (isTransitioning) return;
    
    setIsTransitioning(true);
    setCurrentSlide(prev => prev + 1);
  }, [isTransitioning]);

  const prevSlide = useCallback(() => {
    if (isTransitioning) return;
    
    setIsTransitioning(true);
    setCurrentSlide(prev => prev - 1);
  }, [isTransitioning]);

  // Helper function to restart auto-play timer
  const restartAutoPlay = useCallback(() => {
    if (autoPlayRef.current) {
      clearInterval(autoPlayRef.current);
    }
    if (isAutoPlaying) {
      autoPlayRef.current = setInterval(nextSlide, 3000);
    }
  }, [isAutoPlaying, nextSlide]);

  const goToSlide = useCallback((index) => {
    if (isTransitioning) return;
    
    // Stop any ongoing transitions first
    setIsTransitioning(false);
    
    // Small delay to ensure clean state
    setTimeout(() => {
      setIsTransitioning(true);
      setCurrentSlide(index);
      
      // Ensure auto-play is enabled when clicking indicators
      setIsAutoPlaying(true);
      restartAutoPlay();
      
      // Focus on the service banner CTA after transition
      setTimeout(() => {
        // Find the currently visible service banner CTA
        const serviceBannerCta = document.querySelector('.service-banner-item .service-banner-cta[tabindex="0"]');
        if (serviceBannerCta) {
          serviceBannerCta.focus();
        }
      }, 600); // Wait for transition to complete
      
      // End transition after animation
      setTimeout(() => {
        setIsTransitioning(false);
      }, 500);
    }, 50);
  }, [isTransitioning, restartAutoPlay, setIsAutoPlaying]);

  // Auto-play functionality
  useEffect(() => {
    if (isAutoPlaying) {
      autoPlayRef.current = setInterval(nextSlide, 3000); // Change slide every 3 seconds
    } else {
      if (autoPlayRef.current) {
        clearInterval(autoPlayRef.current);
      }
    }

    return () => {
      if (autoPlayRef.current) {
        clearInterval(autoPlayRef.current);
      }
    };
  }, [isAutoPlaying, nextSlide]);

  // Pause auto-play on hover
  const handleCarouselMouseEnter = () => {
    setIsAutoPlaying(false);
  };

  const handleCarouselMouseLeave = () => {
    setIsAutoPlaying(true);
  };

  // Navigation handlers
  const handlePrevSlide = () => {
    prevSlide();
    // Ensure auto-play is enabled when using navigation
    setIsAutoPlaying(true);
    restartAutoPlay();
  };

  const handleNextSlide = () => {
    nextSlide();
    // Ensure auto-play is enabled when using navigation
    setIsAutoPlaying(true);
    restartAutoPlay();
  };

  // Touch handlers for mobile
  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isRightSwipe = distance > 50;
    const isLeftSwipe = distance < -50;

    if (isRightSwipe) {
      handleNextSlide();
    } else if (isLeftSwipe) {
      handlePrevSlide();
    }
  };

  return (
    <section id="services" className="section services pad-t-0" aria-labelledby="services-title">
      <div className="container">
        {/* Call to Action */}
        <div className="services-cta-wrapper">
          <header className="services-header margin-t-6">
            <h2 id="services-title" ref={titleRef} className="section-title">
              Our <span className="txtWhite">Services</span>
            </h2>
            <p ref={ctaTextRef} className="section-subtitle txtBlack">
              From concept to completion, we deliver exceptional results that exceed your expectations.
            </p>
          </header>
          
          {/* Services Carousel */}
          <div 
            className="services-carousel"
            onMouseEnter={handleCarouselMouseEnter}
            onMouseLeave={handleCarouselMouseLeave}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onKeyDown={(e) => {
              // Prevent any keyboard events from interfering with carousel
              // Only allow Tab key to pass through for accessibility
              if (e.key !== 'Tab') {
                e.preventDefault();
                e.stopPropagation();
              }
            }}
           >
            <div className="carousel-container">
              <div 
                className="carousel-track"
                style={{
                  transform: `translateX(-${(currentSlide + 1) * 16.666}%)`,
                  transition: isTransitioning ? 'transform 0.5s ease-in-out' : 'none'
                }}
                onTransitionEnd={() => {
                  // Handle seamless loop after transition completes
                  if (currentSlide === services.length) {
                    // Jump to first slide without transition
                    setIsTransitioning(false);
                    setTimeout(() => {
                      setCurrentSlide(0);
                    }, 50);
                  } else if (currentSlide === -1) {
                    // Jump to last slide without transition
                    setIsTransitioning(false);
                    setTimeout(() => {
                      setCurrentSlide(services.length - 1);
                    }, 50);
                  } else {
                    // Normal transition end
                    setIsTransitioning(false);
                  }
                }}
              >
                {/* Duplicate last slide at the beginning */}
                {services.map((service, index) => {
                  if (index === services.length - 1) {
                    const serviceData = getServiceData(service);
                    // This duplicate slide should never be tabbable to avoid conflicts
                    const isVisible = false; // Never make duplicate slides tabbable
                    return (
                      <LazyServiceBanner
                        key={`duplicate-last-${index}`}
                        service={service}
                        serviceData={serviceData}
                        onClick={(e) => handleServiceClick(service, e)}
                        tabbable={isVisible}
                      />
                    );
                  }
                  return null;
                })}
                
                {/* Original slides */}
                {services.map((service, index) => {
                  const serviceData = getServiceData(service);
                  // Only make the current slide tabbable
                  const isVisible = index === currentSlide;
                  return (
                    <LazyServiceBanner
                      key={index}
                      service={service}
                      serviceData={serviceData}
                      onClick={() => handleServiceClick(service)}
                      tabbable={isVisible}
                    />
                  );
                })}
                
                {/* Duplicate first slide at the end */}
                {services.map((service, index) => {
                  if (index === 0) {
                    const serviceData = getServiceData(service);
                    // This duplicate slide should never be tabbable to avoid conflicts
                    const isVisible = false; // Never make duplicate slides tabbable
                    return (
                      <LazyServiceBanner
                        key={`duplicate-first-${index}`}
                        service={service}
                        serviceData={serviceData}
                        onClick={(e) => handleServiceClick(service, e)}
                        tabbable={isVisible}
                      />
                    );
                  }
                  return null;
                })}
              </div>
            </div>

            {/* Service Name Indicators */}
            <div className="carousel-indicators" role="list" aria-label="Service navigation">
              {services.map((service, index) => {
                // Calculate the actual displayed slide index
                const actualSlide = currentSlide === services.length ? 0 : 
                                   currentSlide === -1 ? services.length - 1 : 
                                   currentSlide;
                
                const isActive = actualSlide === index;
                
                return (
                  <button
                    key={index}
                    className={`carousel-indicator ${isActive ? 'active' : ''}`}
                    onClick={() => goToSlide(index)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        goToSlide(index);
                      }
                    }}
                    role="listitem"
                    aria-label={`Go to ${service} service`}
                    aria-current={isActive ? 'true' : 'false'}
                  >
                    {service}
                  </button>
                );
              })}
            </div>
          </div>
          <div ref={servicesCtaRef} className="services-cta">
            <h3>Ready to Transform Your Space?</h3>
            <div className="services-cta-buttons">
              <button 
                className="services-cta-button secondary"
                onClick={() => {
                  navigate('/all-services');
                }}
              >
                View All Services
              </button>
              <button 
                className="services-cta-button primary"
                onClick={scrollToFinancingForm}
              >
                Schedule Free Estimate
              </button>
            </div>
          </div>
          
          {/* General Contractor Services Promotion */}
          <div ref={gcPromotionRef} className="gc-promotion">
            <div className="gc-promotion-content">
              <h3 className='pad-h-sm-6'>Investor Project Oversight & General Contracting</h3>
              <p>Whether you're an out-of-state investor, local developer, or property owner managing multiple renovations, having the right general contractor can make or break your project's success.</p>
              <div className="gc-promotion-features">
                <div className="gc-feature">
                  <FaHome className="gc-feature-icon" />
                  <span>Full-Scope General Contracting</span>
                </div>
                <div className="gc-feature">
                  <FaBolt className="gc-feature-icon" />
                  <span>Crew Oversight & Project Auditing</span>
                </div>
                <div className="gc-feature">
                  <FaCreditCard className="gc-feature-icon" />
                  <span>Progress Reports & Accountability</span>
                </div>
              </div>
              <button 
                className="gc-promotion-button"
                onClick={() => navigate('/gc-services')}
              >
                Learn More About GC Services
              </button>
            </div>
          </div>
        </div>

        {/* Financing Section */}
        <div className="financing-section">
          <div className="financing-main same-height" ref={financingHeightRef}>
            <div className="financing-content" data-same-height="1" same-height-device="d">
              <div className="financing-badge">
                AVAILABLE NOW!
              </div>
              
              <div className="financing-header margin-t-xs-3">
                <h3 ref={financingHeaderRef} className="slide-in-left">100% FINANCING</h3>
                <p>Don't wait! Get your dream renovation with <strong>ZERO down payment</strong> and start building equity today.</p>
              </div>
              
              <div className="financing-features">
                <div className="financing-feature">
                  <FaBolt className="feature-icon" />
                  <div>
                    <h4>Quick Approval</h4>
                    <p>Fast, hassle-free approval.</p>
                  </div>
                </div>
                <div className="financing-feature">
                  <FaCreditCard className="feature-icon" />
                  <div>
                    <h4>No Credit Check</h4>
                    <p>Flexible approval process</p>
                  </div>
                </div>
                <div className="financing-feature">
                  <FaHome className="feature-icon" />
                  <div>
                    <h4>Start Today</h4>
                    <p>Begin your project immediately</p>
                  </div>
                </div>
              </div>
              
              <div className="financing-cta">
                <p>Ready to transform your home?</p>
                <span className='txtWhite'>Free quote and financing approval made easy for you!</span>
              </div>
            </div>
            
            <div className="contact-form-card" data-same-height="1" same-height-device="d">
              <div className="form-header">
                <h3>Get Pre-Approved Now</h3>
                <p className="pad-h-xs-4 margin-b-1">No obligation • Quick response • 100% secure</p>
              </div>
              
              <form className="contact-form" onSubmit={handleSubmit} noValidate>
                <div>
                  <div className="form-group">
                    <label htmlFor="financing-name" className="form-label">Full Name</label>
                    <input
                      type="text"
                      id="financing-name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      onKeyDown={handleKeyDown}
                      placeholder="Full Name"
                      className={`form-input ${formErrors.name ? 'error' : ''}`}
                      aria-describedby={formErrors.name ? 'name-error' : undefined}
                      aria-invalid={!!formErrors.name}
                      title="Please enter only letters"
                      required
                    />
                    {formErrors.name && (
                      <span id="name-error" className="form-error" role="alert">{formErrors.name}</span>
                    )}
                  </div>
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="financing-email" className="form-label">Email Address</label>
                    <input
                      type="email"
                      id="financing-email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="Email Address"
                      className={`form-input ${formErrors.email ? 'error' : ''}`}
                      aria-describedby={formErrors.email ? 'email-error' : undefined}
                      aria-invalid={!!formErrors.email}
                      required
                    />
                    {formErrors.email && (
                      <span id="email-error" className="form-error" role="alert">{formErrors.email}</span>
                    )}
                  </div>
                  <div className="form-group">
                    <label htmlFor="financing-phone" className="form-label">Phone Number</label>
                    <input
                      type="tel"
                      id="financing-phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      onKeyDown={handleKeyDown}
                      placeholder="Phone Number"
                      className={`form-input ${formErrors.phone ? 'error' : ''}`}
                      aria-describedby={formErrors.phone ? 'phone-error' : undefined}
                      aria-invalid={!!formErrors.phone}
                      title="Please enter only numbers"
                      required
                    />
                    {formErrors.phone && (
                      <span id="phone-error" className="form-error" role="alert">{formErrors.phone}</span>
                    )}
                  </div>
                </div>

                <div>
                <div className="form-group">
                    {/* Selected Services Tags */}
                    {formData.services.length > 0 && (
                      <div className="selected-services-container">
                        <label className="selected-services-label">Selected Services:</label>
                        <div className="selected-services" ref={financingSelectedServicesRef}>
                          {formData.services.map(serviceValue => {
                            const service = allServices.find(s => s.value === serviceValue);
                            return (
                              <div key={serviceValue} className="service-tag">
                                <span className="service-tag-text">{service?.label || serviceValue}</span>
                                <button
                                  type="button"
                                  className="service-tag-remove"
                                  onClick={() => handleRemoveService(serviceValue)}
                                  aria-label={`Remove ${service?.label || serviceValue}`}
                                >
                                  ×
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    
                    {/* Service Search Input */}
                    {availableServices.length > 0 && (
                      <div 
                        className="service-search-container"
                        onClick={(e) => {
                          // Prevent container clicks from triggering focus
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                      >
                        <label htmlFor="service-search-financing" className="form-label">
                          Services Needed
                        </label>
                        <div className="service-search-input-wrapper">
                          <input
                            ref={serviceSearchRef}
                            type="text"
                            id="service-search-financing"
                            name="service-search"
                            value={serviceSearch}
                            onChange={handleServiceSearchChange}
                            onFocus={handleServiceSearchFocus}
                            onBlur={handleServiceSearchBlur}
                            onKeyDown={handleServiceSearchKeyDown}
                            placeholder="Search and add services..."
                            className={`service-search-input ${formErrors.services ? 'error' : ''}`}
                            autoComplete="off"
                            aria-expanded={showServiceResults}
                            aria-haspopup="listbox"
                            aria-autocomplete="list"
                            aria-describedby={formErrors.services ? 'services-error' : 'services-help'}
                            aria-invalid={!!formErrors.services}
                            role="combobox"
                          />
                          {showServiceResults && (
                            <button
                              type="button"
                              className="service-search-clear-btn"
                              onClick={handleClearServiceSearch}
                              aria-label="Clear search"
                              title="Clear search"
                            >
                              <FaTimesCircle />
                            </button>
                          )}
                        </div>
                        
                        <div id="services-help" className="form-help-text sr-only">
                          Type to search and select services. You can add multiple services.
                        </div>
                        
                        {formErrors.services && (
                          <span id="services-error" className="form-error" role="alert">{formErrors.services}</span>
                        )}
                        
                        {/* Search Results Dropdown */}
                        {(showServiceResults || (serviceSearch.trim() && filteredServices.length === 0)) && (
                          <div 
                            className="service-search-results"
                            role="listbox"
                            aria-label="Available services"
                            id="service-search-listbox-contact"
                          >
                            {serviceSearch.trim() && filteredServices.length === 0 ? (
                              <div 
                                className="service-search-no-results"
                                role="status"
                                aria-live="polite"
                              >
                                No services found for "{serviceSearch}"
                              </div>
                            ) : (
                              (serviceSearch.trim() ? filteredServices : availableServices).map((service, index) => (
                                <div
                                  key={service.value}
                                  className={`service-search-result ${index === selectedOptionIndex ? 'selected' : ''}`}
                                  onClick={() => handleServiceSelect(service.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      handleServiceSelect(service.value);
                                    }
                                  }}
                                  role="option"
                                  tabIndex={-1}
                                  aria-selected={index === selectedOptionIndex}
                                  ref={(el) => {
                                    if (index === selectedOptionIndex && el) {
                                      el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
                                    }
                                  }}
                                >
                                  {service.label}
                                </div>
                              ))
                            )}
                          </div>
                        )}
                      </div>
                    )}
                    
                    {availableServices.length === 0 && formData.services.length > 0 && (
                      <div className="no-services-message">
                        All services have been selected
                      </div>
                    )}
                    
                  </div>
                </div>

                {/* SMS Consent Checkbox */}
                <div className="form-group">
                  <div className="checkbox-container pad-h-3">
                    <input
                      type="checkbox"
                      id="financing-sms-consent"
                      name="smsConsent"
                      checked={formData.smsConsent || false}
                      onChange={handleChange}
                      className={`sms-consent-checkbox ${formErrors.smsConsent ? 'error' : ''}`}
                      aria-describedby="sms-consent-disclosure"
                      aria-invalid={!!formErrors.smsConsent}
                    />
                    <label htmlFor="financing-sms-consent" className="sms-consent-label">
                      <span className="checkbox-text">
                        <p className="txtYellow"><strong>Receive SMS Updates</strong></p>
                        <span className="disclosure-text">
                        By checking this box, you agree to receive SMS messages from RC Remodel. Message and data rates may apply. Frequency may vary. Reply STOP to unsubscribe or HELP for info. Messages may include updates, reminders, notifications, and promotions. View our Terms and Privacy Policy.<br />View our{' '}
                          <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">terms and privacy policy</a>.
                        </span>
                      </span>
                    </label>
                  </div>
                  {formErrors.smsConsent && (
                    <span id="sms-consent-error" className="form-error" role="alert">{formErrors.smsConsent}</span>
                  )}
                </div>
                
                <div className="form-actions">
                  <button 
                    type="submit" 
                    className="financing-submit-button"
                    disabled={isSubmitting}
                  >
                    <span className="button-text">
                      {isSubmitting ? 'Sending...' : 'SCHEDULE YOUR FREE ESTIMATE'}
                    </span>
                  </button>
                </div>
                
                <div className="form-disclaimer">
                  <p>By submitting this form, you agree to receive communications from RC Remodel. We respect your privacy and will never share your information.</p>
                </div>
                
                {/* Success Message */}
                {submitStatus === 'success' && (
                  <div 
                    className={`status-message success ${successMessageFading ? 'fade-out' : ''}`}
                    tabIndex="-1"
                    role="alert"
                    aria-live="polite"
                    style={{ 
                      background: 'rgba(40, 167, 69, 0.1)', 
                      border: '1px solid rgba(40, 167, 69, 0.3)', 
                      color: '#28a745',
                      padding: '15px',
                      borderRadius: '8px',
                      margin: '15px 0',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px'
                    }}
                  >
                    <span className="status-icon">✓</span>
                    <span className="status-text">Your message has been sent successfully!</span>
                  </div>
                )}
                
                {/* Error Message */}
                {submitStatus === 'error' && (
                  <div 
                    className="status-message error"
                    tabIndex="-1"
                    role="alert"
                    aria-live="polite"
                    style={{ 
                      background: 'rgba(220, 38, 38, 0.1)', 
                      border: '1px solid rgba(220, 38, 38, 0.3)', 
                      color: '#dc2626',
                      padding: '15px',
                      borderRadius: '8px',
                      margin: '15px 0',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px'
                    }}
                  >
                    <span className="status-icon">✗</span>
                    <span className="status-text">Sorry, there was an error sending your message. Please try again.</span>
                  </div>
                )}
                
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Service Modal */}
      {isModalOpen && modalService && (
        <div className="service-modal-overlay" onClick={closeModal}>
          <div className="service-modal" ref={modalRef} onClick={(e) => e.stopPropagation()}>
            <div className="service-modal-header">
              <h2 className="service-modal-title">{modalService.service}</h2>
              <button className="service-modal-close" onClick={closeModal} aria-label="Close modal">
                <FaTimes />
              </button>
            </div>
            
            {/* Service Image Container - Below header */}
            <div className="service-modal-image">
              <div className="service-image-container">
                <img 
                  className="service-modal-img"
                  src={modalService.data.imageUrl} 
                  alt={modalService.service}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div className="image-placeholder" style={{display: 'none'}}>
                  <div className="placeholder-icon">🔧</div>
                </div>
                <div className="service-image-overlay">
                  <div className="service-badge">
                    <span className="badge-text">{modalService.data.badgeText || modalService.service}</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="service-modal-content">
              <div className="service-modal-description">
                <p>{modalService.data.description}</p>
              </div>
              
              <div className="service-modal-features">
                <h3>What We Offer:</h3>
                <ul>
                  {modalService.data.features.map((feature, index) => (
                    <li key={index}>{feature}</li>
                  ))}
                </ul>
              </div>
            </div>
            
            <div className="service-modal-cta">
              <button 
                className="btn btn-primary"
                onClick={() => {
                  closeModal();
                  scrollToFinancingForm();
                }}
              >
                Get a Quote
              </button>
              <button 
                className="btn btn-secondary"
                onClick={() => {
                  closeModal();
                  navigate('/');
                  setTimeout(() => {
                    const contactSection = document.getElementById('contact');
                    if (contactSection) {
                      contactSection.scrollIntoView({ 
                        behavior: 'smooth',
                        block: 'start'
                      });
                    }
                  }, 100);
                }}
              >
                Contact Us
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ReCAPTCHA Modal */}
      <ReCaptchaModal
        isOpen={showCaptchaModal}
        onClose={() => setShowCaptchaModal(false)}
        onVerify={handleCaptchaVerify}
        isLoading={isSubmitting}
      />
    </section>
  );
};

export default Services;